import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";

import { formatResetDate } from "@ats/src/views/accountAdmin/accountPlatoAi/aiSubscriptionHelpers";

interface CancelAiCreditSubscriptionConfirmModalProps {
  onCancel: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  planName: string;
  periodEndsAt?: number | null;
}

function CancelAiCreditSubscriptionConfirmModal({
  onCancel,
  onConfirm,
  isLoading = false,
  planName,
  periodEndsAt,
}: CancelAiCreditSubscriptionConfirmModalProps) {
  return (
    <CenterModal headerTitleText="Cancel subscription?" onCancel={onCancel} hasUnsavedChanges={false}>
      <Styled.Body>
        Your {planName} subscription will stop renewing. You keep the credits already on your
        balance and won&rsquo;t be charged again. You just won&rsquo;t receive new subscription
        credits after {formatResetDate(periodEndsAt)}.
      </Styled.Body>

      <Styled.Callout>
        <Icon name="calendar" />
        <Styled.CalloutContent>
          <Styled.CalloutTitle>Will not renew on {formatResetDate(periodEndsAt)}</Styled.CalloutTitle>
          <Styled.CalloutSub>No new subscription credits after this date.</Styled.CalloutSub>
        </Styled.CalloutContent>
      </Styled.Callout>

      <Styled.ButtonContainer>
        <Button styleType="primary" onClick={onConfirm} loading={isLoading}>
          Cancel subscription
        </Button>
        <Button styleType="secondary" onClick={onCancel}>
          Keep subscription
        </Button>
      </Styled.ButtonContainer>
    </CenterModal>
  );
}

export default CancelAiCreditSubscriptionConfirmModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Body = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    label: CancelAiCreditSubscriptionConfirmModal_Body;
    ${[t.text.sm, t.mt(1)]}
    line-height: 1.55;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Callout = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: CancelAiCreditSubscriptionConfirmModal_Callout;
    ${[t.rounded.sm, t.p(4), t.mt(4)]}
    display: flex;
    align-items: center;
    gap: 12px;
    background: transparent;
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};

    svg {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});

Styled.CalloutContent = styled.div`
  label: CancelAiCreditSubscriptionConfirmModal_CalloutContent;
  display: flex;
  flex-direction: column;
  height: 44px;
  justify-content: space-between;
`;

Styled.CalloutTitle = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: CancelAiCreditSubscriptionConfirmModal_CalloutTitle;
    ${[t.text.sm, t.text.bold]}
    color: ${t.dark ? t.color.gray[100] : t.color.black};
  `;
});

Styled.CalloutSub = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: CancelAiCreditSubscriptionConfirmModal_CalloutSub;
    ${[t.text.xs, t.mt("px")]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.ButtonContainer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: CancelAiCreditSubscriptionConfirmModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}
      &:last-of-type {
        ${t.mr(0)}
      }
    }
    ${t.mt(6)}
  `;
});
